import { ExtendedFile } from "../../../shared/interfaces/extended-file";

// Shown when the request window is gone server-side — process 410 or polling
// UploadExpired. The request can't be reused and its actions are disabled, so
// the guidance points at starting over. (Upload PUT failures can't be
// classified as expiry, so they don't use this — see uploadToBlobFailure.)
export const UPLOAD_WINDOW_EXPIRED_MESSAGE =
  "The upload window expired before the file finished uploading. Please start over.";

export interface DocumentUploadResponse {
  id: string;
  callbackUrl: string;
}

// v1: Pending | Completed | Failed. v2 adds AwaitingUpload | UploadExpired.
export type DocumentResultStatus =
  "Pending" | "Completed" | "Failed" | "AwaitingUpload" | "UploadExpired";

export interface DocumentResultResponse {
  id: string;
  status: DocumentResultStatus;
  // Null on v2 when the inline result exceeds the API's size cap; fetch from
  // resultsBlobUri instead. Always populated on v1.
  results: AnalysisResult | null;
  failureReason: string;
  error: AnalysisError;
  additionalContent: {
    wordsBlobUri: string;
    pdfBlobUri: string;
    tablesBlobUri: string;
    figuresBlobUri?: string;
  };
  expired: boolean;
  // v2 only: lightweight metadata available even when results is null.
  resultsSummary?: AnalysisResultsSummary;
  // v2 only: SAS URI to the full results JSON when too large to inline.
  resultsBlobUri?: string;
  // v2 only: present for audio analysis requests, null otherwise.
  audioContent?: {
    audioBlobUri: string;
    transcriptionBlobUri: string;
  } | null;
}

// Normalized transcription blob (DU's schema, not Azure's raw shape) written
// by the backend audio pipeline. `speaker` is an opaque string — consumers
// must not parse, sort, or compare it across transcripts; equal values only
// mean the same speaker within one transcript.
export interface NormalizedSegment {
  id: number;
  speaker: string | null;
  startMs: number;
  endMs: number;
  text: string;
}

export interface NormalizedTranscription {
  durationMs: number;
  segments: NormalizedSegment[];
}

// View-model consumed by the transcript UI: ms converted to sec for direct
// use against the audio element's currentTime.
export interface TranscriptSegment {
  id: number;
  startSec: number;
  endSec: number;
  speaker: string | null;
  text: string;
}

// Shared by transcriptSegmentsSelector and AudioResultComponent (which takes
// the raw transcription as an input, not the selector's output) so the
// sort/convert logic only lives in one place. Sorted ascending by startSec —
// callers (e.g. TranscriptViewerComponent's binary search) rely on that order.
export function toTranscriptSegments(
  transcription: NormalizedTranscription | null
): TranscriptSegment[] {
  return transcription
    ? [...transcription.segments]
        .sort((a, b) => a.startMs - b.startMs)
        .map(s => ({
          id: s.id,
          startSec: s.startMs / 1000,
          endSec: s.endMs / 1000,
          speaker: s.speaker,
          text: s.text,
        }))
    : [];
}

// For a document analyzed with both a PDF/text pipeline and an audio
// pipeline, each text chunk's content is prefixed by the audio pipeline with
// the speaker + timestamp it was transcribed from, e.g.
// "Speaker 2 [00:00:27.740] some spoken text..." — extracted here (rather
// than added as a structured field) since it's backend-formatted text, not a
// separate API field. Lets a chunk click also seek the audio player to the
// same point it highlights on the PDF. Chunks from a document with no audio
// component never contain this pattern, so this safely returns null for them.
const CHUNK_TIMESTAMP_PATTERN = /\[(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?\]/;

export function parseChunkAudioTimestamp(
  content: string | undefined
): number | null {
  const match = content?.match(CHUNK_TIMESTAMP_PATTERN);
  if (!match) return null;

  const [, hours, minutes, seconds, fractional] = match;
  const fractionalSeconds = fractional ? Number(`0.${fractional}`) : 0;
  return (
    Number(hours) * 3600 +
    Number(minutes) * 60 +
    Number(seconds) +
    fractionalSeconds
  );
}

// Reverse of the lookup above: given a playback time, find the chunk whose
// audio prefix most recently started before it. Only chunks that carry their
// own "Speaker N [...]" prefix are considered "anchors" — a page-continuation
// fragment (the tail of a passage split across a PDF page boundary) has no
// timestamp of its own and would only add ambiguity about which page to jump
// to, so it's deliberately excluded here. Returns null before the first
// timestamped chunk plays, including for a PDF-only document with no audio
// prefixes at all.
export function findChunkAtAudioTime(
  textChunks: TextChunk[],
  timeSeconds: number
): TextChunk | null {
  const timestamped = textChunks
    .map(chunk => ({ chunk, onset: parseChunkAudioTimestamp(chunk.content) }))
    .filter(
      (entry): entry is { chunk: TextChunk; onset: number } =>
        entry.onset !== null
    );

  // Rightmost entry whose onset <= timeSeconds — mirrors
  // TranscriptViewerComponent.binarySearchStart's "nearest preceding" search,
  // just over timestamped chunks instead of transcript segments.
  let lo = 0;
  let hi = timestamped.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (timestamped[mid].onset <= timeSeconds) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result >= 0 ? timestamped[result].chunk : null;
}

export interface InitializeUploadResponse {
  requestId: string;
  uploadUrl: string;
  expiresAt: string;
  maxFileSizeBytes: number;
  allowedFileExtensions: string[];
}

// Model/extraction settings sent to the v2 process endpoint. Carried on the
// action (not written onto the file) because the file is frozen in the store.
export interface ProcessAnalysisOptions {
  model: string;
  outputContentFormat?: string;
  includeLineNumbers: boolean;
  enableComments: boolean;
}

// The active v2 upload session shown to the user between initialize-upload and
// process: the SAS URL they can upload to (via the portal or directly to Azure)
// and the request id used to enqueue processing.
export interface UploadSession {
  requestId: string;
  uploadUrl: string;
  expiresAt: string;
}

// Where the v2 stepped upload flow currently is. v1/Pandoc never leave "idle".
export type UploadStep =
  "idle" | "initializing" | "awaitingUpload" | "processing";

// Status of the optional "upload via portal" action (PUT to the SAS URL). When
// the user uploads directly to Azure instead, this stays "idle".
// "expired" = the SAS window is gone; the panel stays visible but its upload
// and process actions are disabled, since the URL can no longer be used.
export type PortalUploadStatus =
  "idle" | "uploading" | "uploaded" | "error" | "expired";

export interface AnalysisResultsSummary {
  pageCount: number;
  bytesProcessed: number;
  ocrScore: number;
  textChunkCount: number;
  estimatedSizeBytes: number;
}

export interface SupportedFileTypesResponse {
  supportedFileTypes: string[];
}

export interface FileSizeLimitResponse {
  maxFileSizeBytes: number;
}

// Result of a server-side HEAD probe against a user-supplied source URL, used
// to auto-fill the file size field in the "analyze from URL" flow. Fields are
// null when the source can't be probed (unreachable, no Content-Length, etc.).
export interface ProbeUrlResponse {
  contentLengthBytes: number | null;
  contentType: string | null;
  reachable: boolean;
  suggestedFileName: string | null;
}

export interface AnalysisResult {
  pageCount: number;
  textChunks: TextChunk[];
  content: string;
  range: Range;
  pages: Page[];
  analysisStagesPerformed: string[];
  bytesProcessed: number;
  ocrScore: number;
  convertedContent?: string;
}

export interface AnalysisError {
  type: string;
  description: string;
}

export interface TableRow {
  rowKey: `${number}-${number}`;
  content: string;
}

interface TextChunkBase {
  index: number;
  type: string;
  content: string;
  pageIndices: number[];
  boundaries: Boundary[];
  rows?: TableRow[];
  printedLineNum?: number[];
  extensions?: {
    panel?: {
      quadrant: string;
      pageNumber?: number;
    };
  };
}

// v1 and v2 are mutually exclusive on range shape: v1 emits a single `range`,
// v2 emits a `ranges` array and drops `range`. Modelled as a discriminated
// union (with `?: never` on the absent field) so the four illegal combinations
// — both set, neither set — fail to type-check.
export interface TextChunkV1 extends TextChunkBase {
  range: Range;
  ranges?: never;
}

export interface TextChunkV2 extends TextChunkBase {
  range?: never;
  ranges: Range[];
  readingOrderIndex?: number;
}

export type TextChunk = TextChunkV1 | TextChunkV2;

export interface Page {
  index: number;
  width: number;
  height: number;
  unit: string;
}

export interface Boundary {
  t: number;
  b: number;
  l: number;
  r: number;
  pageIndex: number;
  polygon: number[];
}

export interface PageHighlight {
  boundary: Boundary;
  label: string;
  chunkIndex: number;
}

interface Range {
  start: number;
  end: number;
}

export interface DocumentUpload {
  fileName: string;
  fileSize: number;
  file: ExtendedFile;
  includeLineNumbers?: boolean;
}

export interface TableCell {
  boundaries: Boundary[];
  row_index: number;
  column_index: number;
  content: string;
  kind: string;
  range?: Range[];
  text_chunks?: number[];
}

export interface TableData {
  boundaries: Boundary[];
  cells: TableCell[];
  column_count: number;
  row_count: number;
  ranges: Range[];
}

export interface FilteredDocumentResult {
  filteredResult: any;
  currentPage: number;
  totalPages: number;
}
